import { TYPO } from "styles/Typo";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "next/navigation";
import { useState } from "react";
import usePortfolioScoreUtil from "utils/portfolio/score";
import Image from "next/image";
import Button from "components/common/buttons/buttons";
import { TAILWIND_TO_CSS } from "utils/constant/STYLE";
import { useRouter } from "next/router";
import { useHandleImageErrorVer2 } from "utils/useHandleImageError";
import { SkeletonBlock3 } from "components/skeleton";
import { trackPageAction } from "lib/analytics/core/actions";
const ScoreDetail = ({ item, last }: { item: any; last: boolean }) => {
  const router = useRouter();
  const isMainPage = router.asPath === "/?type=portfolio";
  const gtmId = isMainPage
    ? "pf-main-score-stock-detail-accordion"
    : "pf-teaser-score-stock-detail-accordion";
  const Section = ({
    title,
    score,
    index,
    scoreTestId,
  }: {
    title: string;
    score: number;
    index: number;
    scoreTestId: string;
  }) => {
    return (
      <div
        className={`px-4 py-3 flex flex-col gap-1 grow flex-1 items-center justify-center ${
          index !== 0 ? "border-l border-gray20" : ""
        }`}
      >
        <p className={`${TYPO.TYPO7_1_RG} text-gray60 text-center`}>{title}</p>
        <p
          className={`${TYPO.TYPO7_1_RG} text-gray80 text-center bg-gray15 rounded-[4px] px-2 w-fit`}
          data-testid={scoreTestId}
        >
          {score}
        </p>
      </div>
    );
  };
  const Meta = [
    {
      title: "산업 상승세",
      score: item?.MOM_PER,
      scoreTestId: "pf-score-card-mmt-score",
    },
    {
      title: "주가 탄력성",
      score: item?.RAR_PER,
      scoreTestId: "pf-score-card-rar-score",
    },
    {
      title: "재무 건전성",
      score: item?.FIN_SCORE,
      scoreTestId: "pf-score-card-fin-score",
    },
  ];

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={` ${
        last ? "" : "border-b border-gray20"
      }  flex w-full overflow-hidden`}
      data-testid={`pf-score-card-detail-section`}
      data-gtm-id={gtmId}
    >
      <div className="flex w-full">
        {Meta.map((item, index) => (
          <Section
            title={item.title}
            score={item.score}
            key={index}
            index={index}
            scoreTestId={item.scoreTestId}
          />
        ))}
      </div>
    </motion.div>
  );
};

function MainPortfolioScore({ portId }: { portId: number | null }) {
  const router = useRouter();
  // const handleImageError = useHandleImageError();
  const { getStockLogoSafe, onError } = useHandleImageErrorVer2();
  const { data, mean, maxBaseDt, isLoading, isError } = usePortfolioScoreUtil(
    Number(portId)
  );
  const [openedIndexes, setOpenedIndexes] = useState<number[]>([0]);

  const handleToggle = (index: number) => {
    trackPageAction(
      openedIndexes.includes(index) ? "collapse_pf_score" : "expand_pf_score"
    );
    setOpenedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const level = mean ? (mean > 50 ? "high" : "low") : "";

  const desc = mean
    ? level === "high"
      ? `좋은 기업 위주로 잘 골랐어요`
      : `아쉬운 기업이 꽤 보여요`
    : ``;

  if ((!data || isLoading) && !isError)
    return (
      <div className="px-4 pt-[30px]">
        <SkeletonBlock3 width={"100%"} height={300} />
      </div>
    );

  return (
    <div>
      {data && (
        <>
          <p className={`px-6 pt-[30px] ${TYPO.TYPO5_SB} text-gray90`}>
            투자매력도
          </p>
          <div className="p-4 flex flex-col gap-[10px]">
            <div className="flex items-center  px-4 py-[10px] bg-1 rounded-[14px] ">
              <div className="flex flex-col gap-1 grow">
                <div className="flex gap-[6px] items-center">
                  <p className={`${TYPO.TYPO7_MD} text-gray90`}>
                    {level === "high" ? "높음" : "낮음"}
                  </p>
                  <p
                    className={`${TYPO.TYPO9_MD} text-gray70 bg-gray20 px-[6px] py-[1px] rounded-[20px]`}
                  >
                    {mean?.toFixed(0)}점
                  </p>
                </div>
                <p
                  className={`whitespace-pre-line ${TYPO.TYPO7_1_RG} text-gray70`}
                >
                  {desc}
                </p>
              </div>
              {/* {mean && mean < 100 && (
              <Button
                type="secondary"
                size="sm"
                clickHandler={goHighScorePage}
                text={"점수 높이기"}
              />
            )} */}
            </div>
            <div className="flex flex-col bg-1 border border-gray10 rounded-[14px] overflow-hidden">
              {data.map((item: any, index: number) => (
                <div key={index}>
                  <div
                    className={`flex items-center gap-2 px-4 py-3 cursor-pointer  transition-colors ${
                      data?.length - 1 > index || openedIndexes.includes(index)
                        ? "border-b border-gray20"
                        : ""
                    }`}
                    onClick={() => handleToggle(index)}
                    data-testid={`pf-score-card-detail-accordion`}
                  >
                    <Image
                      src={getStockLogoSafe(item.ITEM_CD_DL)}
                      alt={item.ITEM_KR_NM}
                      key={`${item.ITEM_CD_DL}-${index}`}
                      width={24}
                      height={24}
                      className="rounded-full w-[24px] h-[24px]"
                      onError={() => onError(item.ITEM_CD_DL)}
                    />
                    <p className={`${TYPO.TYPO7_RG} text-gray70 grow`}>
                      {item.ITEM_KR_NM}
                    </p>
                    <p className={`${TYPO.TYPO7_MD} text-gray90`}>
                      {item.TOTAL_PER}점
                    </p>
                    <motion.div
                      animate={{
                        rotate: openedIndexes.includes(index) ? 180 : 0,
                      }}
                      transition={{ duration: 0.2 }}
                      className="w-3 h-3 flex items-center justify-center"
                    >
                      <svg
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.5L6 6.5L11 1.5"
                          stroke={TAILWIND_TO_CSS["gray40"]}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  </div>
                  <AnimatePresence>
                    {openedIndexes.includes(index) && (
                      <ScoreDetail
                        item={item}
                        last={data?.length - 1 === index}
                      />
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            {/* {maxBaseDt && (
            <p className={`${TYPO.TYPO8_RG} text-gray60 text-right`}>
              마지막 업데이트 ・ {maxBaseDt}
            </p>
          )} */}
          </div>
        </>
      )}
    </div>
  );
}
export const Question = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11ZM5.26626 7.15168C5.27443 6.11435 5.56848 5.7958 6.08306 5.47725C6.43428 5.25672 6.69565 4.97901 6.69565 4.57061C6.69565 4.11321 6.34443 3.81916 5.89519 3.81916C5.4868 3.81916 5.09474 4.08054 5.07023 4.62779H4.04924C4.07375 3.53328 4.89871 2.9697 5.90336 2.9697C7.00603 2.9697 7.76565 3.58229 7.76565 4.55428C7.76565 5.21588 7.43077 5.64878 6.89985 5.96733C6.42611 6.25321 6.23008 6.53908 6.22191 7.15168V7.22519H5.26626V7.15168ZM5.77268 9.0303C5.42146 9.0303 5.13558 8.74443 5.13558 8.39321C5.13558 8.05015 5.42146 7.76427 5.77268 7.76427C6.10756 7.76427 6.40161 8.05015 6.40161 8.39321C6.40161 8.74443 6.10756 9.0303 5.77268 9.0303Z"
        fill="var(--gray40)"
      />
    </svg>
  );
};

export default MainPortfolioScore;
